import FacebookIcon from '@mui/icons-material/Facebook'
import InstagramIcon from '@mui/icons-material/Instagram'
import PinterestIcon from '@mui/icons-material/Pinterest'
import YouTubeIcon from '@mui/icons-material/YouTube'
import XIcon from '@mui/icons-material/X'
import { Box, Grid, SxProps, Theme, Typography, styled, useTheme } from '@mui/material'
import ExternalLink from '@mui/material/Link'
import { Link } from 'react-router-dom'
import { EXTERNAL_ROUTES } from '../../../config/routes'
import Logo from '../../../svg/Logo'

const StyledFooter = styled('footer')(({ theme }) => ({
  padding: '0 20px',
  textAlign: 'left',
  backgroundColor: '#fff',
  [theme.breakpoints.up('lg')]: {
    padding: '0 140px'
  }
}))

const StyledSocialLink = styled(ExternalLink)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: '32px',
  height: '32px',
  backgroundColor: '#344054',
  color: '#fff',
  borderRadius: '100px',
  '&:hover': {
    background: '#e9edf9',
    color: '#1C47CB'
  },
  [theme.breakpoints.up('sm')]: {
    width: '50px',
    height: '50px'
  }
})) as typeof ExternalLink

const StyledListSubheader = styled('h2')(({ theme }) => ({
  fontFamily: '"Outfit", sans-serif',
  fontSize: '1.125rem',
  fontWeight: 600,
  color: '#101828',
  marginBottom: '22px',
  [theme.breakpoints.up('md')]: {
    fontSize: '1.25rem'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.375rem'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.5rem'
  }
}))

const StyledList = styled('ul')(() => ({
  padding: 0,
  margin: 0,
  listStyle: 'none',
  '& li': {
    marginBottom: '20px',
    '&:last-of-type': {
      marginBottom: 0
    }
  }
}))

const StyledTextLink = styled(ExternalLink)(({ theme }) => ({
  textDecoration: 'none',
  color: '#344054',
  fontFamily: '"proxima-nova", sans-serif',
  fontSize: '1rem',
  fontWeight: 400,
  '&:hover': {
    textDecoration: 'underline'
  },
  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.25rem'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.375rem'
  }
})) as typeof ExternalLink

const Copyright = styled('small')(({ theme }) => ({
  marginTop: 36,
  marginBottom: 36,
  display: 'block',
  fontSize: '1rem',
  fontFamily: '"proxima-nova", sans-serif',
  fontWeight: 400,
  textAlign: 'center',
  padding: '10px 20px 20px',
  [theme.breakpoints.up('md')]: {
    fontSize: '1.125rem'
  },
  [theme.breakpoints.up('lg')]: {
    fontSize: '1.25rem',
    padding: '10px 100px 40px'
  },
  [theme.breakpoints.up('xl')]: {
    fontSize: '1.375rem'
  }
}))

const socIconStyles: SxProps<Theme> = {
  fontSize: { xs: '1.125rem', sm: '1.5rem' }
}

export default function Footer() {
  const theme = useTheme()

  return (
    <StyledFooter>
      <Grid
        container
        spacing={1}
        sx={{
          borderTop: '1px solid #E4E7EC',
          borderBottom: '1px solid #E4E7EC',
          padding: { xs: '40px 0', sm: '60px 0', md: '80px 0' },
          maxWidth: theme.breakpoints.values.xl,
          margin: { xs: '20px auto 0', sm: '20px 0 0', md: '20px auto 0' }
        }}
      >
        <Grid item xs={12} md={5}>
          <Box sx={{ margin: { xs: '0 auto', sm: 0 }, width: { xs: '200px', md: '175px', lg: '225px' } }}>
            <Logo alignment="flex-start" />
          </Box>
          <Typography
            sx={{
              color: '#626262',
              fontFamily: '"proxima-nova", sans-serif',
              fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
              fontWeight: 400,
              textAlign: { xs: 'center', sm: 'left' },
              margin: '20px 0'
            }}
          >
            Follow us on social media:
          </Typography>
          <Box component="nav" sx={{ display: 'flex', gap: { xs: '10px', sm: '20px' }, justifyContent: { xs: 'center', sm: 'flex-start' } }}>
            <StyledSocialLink
              href="https://facebook.com/photobucket?utm_medium=web&utm_source=footer&utm_content=socialIcons&utm_campaign=v2_facebook"
              target="_blank"
              aria-label="Facebook"
            >
              <FacebookIcon sx={socIconStyles} />
            </StyledSocialLink>
            <StyledSocialLink
              href="https://instagram.com/photobucket?utm_medium=web&utm_source=footer&utm_content=socialIcons&utm_campaign=v2_instagram"
              target="_blank"
              aria-label="Instagram"
            >
              <InstagramIcon sx={socIconStyles} />
            </StyledSocialLink>
            <StyledSocialLink
              href="https://pinterest.com/photobucket?utm_medium=web&utm_source=footer&utm_content=socialIcons&utm_campaign=v2_pinterest"
              target="_blank"
              aria-label="Pinterest"
            >
              <PinterestIcon sx={socIconStyles} />
            </StyledSocialLink>
            <StyledSocialLink
              href="https://twitter.com/photobucket?utm_medium=web&utm_source=footer&utm_content=socialIcons&utm_campaign=v2_twitter"
              target="_blank"
              aria-label="Twitter"
            >
              <XIcon sx={socIconStyles} />
            </StyledSocialLink>
            <StyledSocialLink
              href="https://www.youtube.com/channel/UC8dQwMWdFV2GaAf4d7OIrcA?utm_medium=web&utm_source=footer&utm_content=socialIcons&utm_campaign=v2_youtube"
              target="_blank"
              aria-label="Youtube"
            >
              <YouTubeIcon sx={socIconStyles} />
            </StyledSocialLink>
          </Box>
        </Grid>
        <Grid item xs={12} md={7}>
          <Box
            sx={{
              display: 'grid',
              gridAutoFlow: { xs: 'row', sm: 'column' },
              gridAutoColumns: '1fr',
              textAlign: { xs: 'center', sm: 'left' },
              gap: { xs: '5px', sm: '20px' },
              justifyContent: 'space-between'
            }}
          >
            <div>
              <StyledListSubheader>Services</StyledListSubheader>
              <nav>
                <StyledList>
                  <li>
                    <StyledTextLink data-test="footer-capture" href={EXTERNAL_ROUTES.PB_CAPTURE}>
                      Digitize Your Memories
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-canvas-prints" href={EXTERNAL_ROUTES.PRINTSHOP_CANVAS_PRINTS} target="_blank">
                      Canvas Prints
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-prints-enlargements" href={EXTERNAL_ROUTES.PRINTSHOP_PRINTS} target="_blank">
                      Enlargements
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-greeting-cards" href={EXTERNAL_ROUTES.PRINTSHOP_CARDS} target="_blank">
                      Greeting Cards
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-photo-books" href={EXTERNAL_ROUTES.PRINTSHOP_PHOTO_BOOKS} target="_blank">
                      Photo Books
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-photo-gifts" href={EXTERNAL_ROUTES.PRINTSHOP_GIFTS} target="_blank">
                      Photo Gifts
                    </StyledTextLink>
                  </li>
                </StyledList>
              </nav>
            </div>
            <div>
              <StyledListSubheader>Get Help</StyledListSubheader>
              <nav>
                <StyledList>
                  <li>
                    <StyledTextLink data-test="footer-help-center" href={EXTERNAL_ROUTES.HELP_CENTER}>
                      Help Center
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink component={Link} to="/auth/forgot-password" data-test="footer-recover-account">
                      Recover Your Account
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-contact-us" href={EXTERNAL_ROUTES.CONTACT_US}>
                      Contact Us
                    </StyledTextLink>
                  </li>
                </StyledList>
              </nav>

              <StyledListSubheader>Discounts</StyledListSubheader>
              <nav>
                <StyledList>
                  <li>
                    <StyledTextLink data-test="footer-student-discounts" href={EXTERNAL_ROUTES.STUDENT_DISCOUNTS}>
                      Student and Graduate
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-other-discounts" href={EXTERNAL_ROUTES.OTHER_DISCOUNTS}>
                      Exclusive Deals
                    </StyledTextLink>
                  </li>
                </StyledList>
              </nav>
            </div>
            <div>
              <StyledListSubheader>Resources</StyledListSubheader>
              <nav>
                <StyledList>
                  <li>
                    <StyledTextLink data-test="footer-contact-us" href={EXTERNAL_ROUTES.BLOG}>
                      Blog
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink component={Link} data-test="footer-accessibility-commitment" to="/accessibility-commitment">
                      Accessibility Commitment
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink data-test="footer-affiliate-program" href={EXTERNAL_ROUTES.AFFILIATE_PROGRAM}>
                      Join Our Affiliate Program
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink component={Link} data-test="footer-privacy" to="/legal/privacy-policy">
                      Privacy Policy
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink component={Link} data-test="footer-terms-of-service" to="/legal/terms-of-service">
                      Terms of Use
                    </StyledTextLink>
                  </li>
                  <li>
                    <StyledTextLink
                      component={Link}
                      data-test="footer-biometric-information-privacy-policy"
                      to="/legal/biometric-information-privacy-policy"
                    >
                      Biometric Policy
                    </StyledTextLink>
                  </li>
                </StyledList>
              </nav>
            </div>
          </Box>
        </Grid>
      </Grid>
      <Box>
        <Copyright>
          &copy; {new Date().getFullYear()} Photobucket.com, Inc. All rights reserved. Photobucket is a registered trademark of Photobucket.com, Inc.
        </Copyright>
      </Box>
    </StyledFooter>
  )
}
